import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StaffLink from '../../components/shared/StaffLink';
import PipelineStage from '../../components/shared/PipelineStage';
import {
  CalendarDaysIcon, SparklesIcon, EnvelopeIcon, ChevronLeftIcon, ChevronRightIcon,
  XMarkIcon, LockOpenIcon, LockClosedIcon, ArrowUpTrayIcon, ArrowUturnLeftIcon,
  ExclamationTriangleIcon, TrashIcon, PencilSquareIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import { FALLBACK_THEME, themeForRoom, sortRooms } from '../../lib/classroomTheme';
import { DATE_FLOOR } from '../../lib/dateBounds';

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
};

// Difference between two HH:MM strings, in minutes. BBC childcare doesn't
// run overnight shifts, so when end < start that's a typo (almost always
// the director meant pm but typed am). Returning 0 keeps a bad row from
// silently inflating the weekly-hours total to 50+ hr.
const diffMinutes = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = String(start).split(':').map(Number);
  const [eh, em] = String(end).split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return 0;
  const m = (eh * 60 + em) - (sh * 60 + sm);
  return m > 0 ? m : 0;
};

// True when a shift's end time is before its start time (or equal). BBC
// doesn't do overnights, so this means the director typo'd am/pm — the
// grid surfaces it so she can fix the shift.
const isInverted = (start, end) => {
  if (!start || !end) return false;
  const [sh, sm] = String(start).split(':').map(Number);
  const [eh, em] = String(end).split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return false;
  return (eh * 60 + em) <= (sh * 60 + sm);
};

// Approximate minutes spent on a logged lunch break — accepts ranges
// like "12-1" or "12:00-1:00". Single times or unparseable strings → 0.
const lunchMinutes = (l) => {
  if (!l) return 0;
  const s = String(l).trim();
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!m) return 0;
  const to24 = (h, ampm) => {
    let n = Number(h);
    if (Number.isNaN(n)) return null;
    if (ampm) {
      const p = ampm.toLowerCase();
      if (p === 'pm' && n < 12) n += 12;
      if (p === 'am' && n === 12) n = 0;
    } else {
      // No am/pm — heuristic: a 1-hour lunch in a daytime shift. If the
      // start is small (e.g. "12-1"), assume start is PM-ish.
      if (n < 7) n += 12;
    }
    return n;
  };
  const sh = to24(m[1], m[3]);
  const eh = to24(m[4], m[6]);
  if (sh == null || eh == null) return 0;
  const sm = Number(m[2] || 0), em = Number(m[5] || 0);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  // Clamp to sane lunch lengths so a typo doesn't blow up the total.
  return Math.max(0, Math.min(mins, 120));
};

// Get the Monday of the week containing the given date.
// BBC operates Mon–Fri, so weeks start Monday and the grid shows 5 days.
const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // getDay(): Sunday=0, Monday=1, …, Saturday=6
  // Distance back to Monday: if Sun, go back 6; otherwise dow-1.
  const dow = d.getDay();
  const back = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - back);
  return d;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const formatISO = (d) => d.toISOString().slice(0, 10);

// Mirror of the server-side lunch-coverage rule so edits in the review
// table re-flag rows live without a round-trip.
const LUNCH_REQUIRED_HOURS = 7;
const lunchScheduled = (l) => {
  if (!l) return false;
  const s = String(l).trim();
  if (!s) return false;
  if (/^(0+(?::0+)*|none|n\/a|—|-|no lunch|remote)$/i.test(s)) return false;
  if (/^\d{1,2}(?::\d{2})?\s*(?:am|pm)?\s*[-–—]\s*\d{1,2}(?::\d{2})?\s*(?:am|pm)?$/i.test(s)) return true;
  if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(s) && !/^0+:00(:00)?$/.test(s)) return true;
  return false;
};
const shiftHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return 0;
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 100) / 100;
};
const recomputeCoverage = (shift) => {
  const hours = shiftHours(shift.start_time, shift.end_time);
  return {
    ...shift,
    shift_hours: hours,
    needs_lunch_coverage: hours >= LUNCH_REQUIRED_HOURS && !lunchScheduled(shift.lunch_break),
  };
};

export default function TeamSchedulePage() {
  const [tab, setTab] = useState('grid');

  return (
    <div className="animate-slide-up">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDaysIcon className="w-6 h-6" /> Team Schedule
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Weekly schedule for the whole team. Paste from Excel to import; clock-ins are locked to 10 min before scheduled start unless you grant an exception.
        </p>
      </header>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { id: 'grid', label: 'Week Grid' },
          { id: 'import', label: 'Import from Excel' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'grid' && <WeekGrid />}
      {tab === 'import' && <ImportFlow onSaved={() => setTab('grid')} />}
    </div>
  );
}

// ─── Week grid ─────────────────────────────────────────────

function WeekGrid() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Classrooms drive the cell colors + the legend at the bottom.
  useEffect(() => {
    api.get('/classrooms').then(({ data: d }) => setClassrooms(d)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team-schedule/week?start=${formatISO(weekStart)}`);
      setData(data);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const toggleEarly = async (overrideId, currently) => {
    try {
      await api.patch(`/team-schedule/overrides/${overrideId}/early-clockin`, { allowed: !currently });
      toast.success(!currently ? 'Early clock-in granted' : 'Early clock-in revoked');
      load();
    } catch { toast.error('Failed'); }
  };

  // Inline-edit state: when set, opens the EditShiftModal pinned to one
  // staff/day cell. `staff` + `date` always present; `cell` is null for a
  // brand-new (currently "Off") cell so the modal opens with blanks.
  const [editing, setEditing] = useState(null); // { staff, date, cell }
  const openEdit = (staff, date, cell) => setEditing({ staff, date, cell });
  const closeEdit = () => setEditing(null);

  // "Add Shift" picker — opens a who/when chooser so admins don't have
  // to know cells are clickable. Once they pick, it routes into the
  // same EditShiftModal with status=working pre-set.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Multi-select for bulk-apply. Keys are `${staffId}|${dayISO}`. Clicks
  // with cmd/ctrl/shift toggle a cell into the selection instead of
  // opening the edit modal — once selection.size > 0 a sticky action
  // bar offers to apply a shift to all selected cells at once.
  const [selection, setSelection] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleSelection = (staff, date) => {
    const key = `${staff.id}|${date}`;
    setSelection(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearSelection = () => setSelection(new Set());

  const applyBulk = async ({ start_time, end_time, classroom, lunch_break }) => {
    if (selection.size === 0) return;
    // When classroom is blank in the bulk modal, fall back to each
    // staff's primary classroom so admins don't have to pick one for
    // the whole batch when they're scheduling within home rooms.
    const staffById = new Map((data?.rows || []).map(r => [r.id, r]));
    const items = [...selection].map(k => {
      const [staffId, date] = k.split('|');
      const sid = Number(staffId);
      const fallback = staffById.get(sid)?.primary_classroom || null;
      return {
        staff_id: sid,
        date,
        classroom: classroom || fallback,
      };
    });
    try {
      await Promise.all(items.map(it => api.post('/team-schedule/overrides', {
        ...it, start_time, end_time, lunch_break, status: 'working',
      })));
      toast.success(`Applied to ${items.length} cell${items.length === 1 ? '' : 's'}`);
      clearSelection();
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk apply failed');
    }
  };

  const saveEdit = async ({ staff_id, date, start_time, end_time, classroom, lunch_break, status, segments }) => {
    const doSave = async (force) => {
      await api.post('/team-schedule/overrides', {
        staff_id, date, start_time, end_time, classroom, lunch_break, status, segments,
        ...(force ? { force: true } : {}),
      });
    };
    try {
      await doSave(false);
      toast.success('Shift saved');
      setEditing(null);
      load();
    } catch (err) {
      const body = err.response?.data;
      if (body?.error === 'partial_time_off_conflict') {
        // Staff has an approved partial time-off window that this shift
        // overlaps. Give the director the exact window in the confirm.
        const to = body.time_off || {};
        const msg = `Heads up — this staff has approved time off from ${formatTime(to.start)} to ${formatTime(to.end)}` +
          (to.reason ? ` ("${to.reason}")` : '') +
          `. The shift you're saving overlaps that window.\n\n` +
          `Click OK to save it anyway (rare — only if you actually want them working through their time off), or Cancel to fix the times.`;
        if (!window.confirm(msg)) return;
        try {
          await doSave(true);
          toast.success('Shift saved (partial time-off overridden)');
          setEditing(null);
          load();
        } catch (err2) {
          toast.error(err2.response?.data?.error || err2.response?.data?.message || 'Save failed');
        }
        return;
      }
      toast.error(body?.error || body?.message || 'Save failed');
    }
  };

  const deleteShift = async (overrideId) => {
    if (!overrideId) { setEditing(null); return; }
    if (!window.confirm('Remove this shift? The cell will go back to Off.')) return;
    try {
      await api.delete(`/team-schedule/overrides/${overrideId}`);
      toast.success('Shift removed');
      setEditing(null);
      load();
    } catch { toast.error('Failed to remove'); }
  };

  const clearWeek = async () => {
    if (!window.confirm(`Wipe every shift for the week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}? This can't be undone.`)) return;
    try {
      const { data: r } = await api.delete(`/team-schedule/week?start=${formatISO(weekStart)}`);
      toast.success(`Cleared ${r.shifts_removed} shift${r.shifts_removed === 1 ? '' : 's'}`);
      load();
    } catch { toast.error('Failed to clear week'); }
  };

  // PRN / float / known-out staff: one click to set the whole Mon–Fri
  // week to Off for that person. Blocks any recurring template too, so
  // the schedule doesn't keep suggesting them.
  const markStaffWeekOff = async (staff) => {
    const label = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!window.confirm(`Mark ${staff.first_name} ${staff.last_name} off for the week of ${label}? Any shifts already scheduled will be overwritten to Off.`)) return;
    try {
      await api.post(`/team-schedule/staff/${staff.id}/mark-week-off`, {
        week_start: formatISO(weekStart),
      });
      toast.success(`${staff.first_name} marked off for the week`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  // Copy last week's grid forward. Skips cells that already have an
  // override in the visible week, so approved time-off / manual entries
  // stay intact. Use "Clear this week" first to overwrite completely.
  const copyFromLastWeek = async () => {
    const sourceMon = addDays(weekStart, -7);
    const sourceLabel = sourceMon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!window.confirm(`Copy last week (${sourceLabel}) into this view? Approved AND pending time-off requests get blocked out automatically first — then last week's shifts fill in around them. Existing cells won't be overwritten.`)) return;
    try {
      const { data: r } = await api.post('/team-schedule/copy-from-week', {
        source_week_start: formatISO(sourceMon),
        target_week_start: formatISO(weekStart),
      });
      const parts = [`Copied ${r.shifts_copied} shift${r.shifts_copied === 1 ? '' : 's'}`];
      const timeOffAdded = (r.time_off_approved_added || 0) + (r.time_off_pending_added || 0);
      if (timeOffAdded > 0) {
        const pieces = [];
        if (r.time_off_approved_added) pieces.push(`${r.time_off_approved_added} approved`);
        if (r.time_off_pending_added) pieces.push(`${r.time_off_pending_added} pending`);
        parts.push(`blocked out ${timeOffAdded} time-off day${timeOffAdded === 1 ? '' : 's'} (${pieces.join(' + ')})`);
      }
      if (r.shifts_skipped) parts.push(`${r.shifts_skipped} skipped (already filled)`);
      toast.success(parts.join(' · '));
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Copy failed'); }
  };

  // Email preview/send. Two-step: GET notify-preview shows the recipient
  // list (with shift counts + missing emails), then POST notify-week
  // actually sends. Director can untick individuals before firing.
  const [emailPreview, setEmailPreview] = useState(null);
  const openEmailPreview = async () => {
    try {
      const { data } = await api.get(`/team-schedule/notify-preview?start=${formatISO(weekStart)}`);
      setEmailPreview(data);
    } catch { toast.error('Failed to load preview'); }
  };
  const sendEmails = async (selectedIds) => {
    try {
      const { data } = await api.post('/team-schedule/notify-week', {
        week_start: formatISO(weekStart),
        staff_ids: selectedIds,
      });
      toast.success(`Sent ${data.sent}${data.failed ? ` · ${data.failed} failed` : ''}`);
      setEmailPreview(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Send failed'); }
  };

  // Re-block approved time off on the currently-viewed week. Useful when
  // shifts were typed / copied BEFORE an approval was in place — approved
  // wins and overwrites the shift. Pending stays additive.
  const syncTimeOff = async () => {
    try {
      const { data: r } = await api.post('/team-schedule/sync-time-off', {
        week_start: formatISO(weekStart),
      });
      const total = (r.time_off_approved_added || 0) + (r.time_off_pending_added || 0);
      const found = Array.isArray(r.requests_found) ? r.requests_found : [];
      const foundLine = found.length === 0
        ? 'No approved or pending requests overlap this week.'
        : `${found.length} request${found.length === 1 ? '' : 's'} on file: ` +
          found.map(x => `${x.staff} (${x.status} ${x.start_date}${x.start_date === x.end_date ? '' : `→${x.end_date}`})`).join(' · ');
      if (total === 0) {
        toast(foundLine, { duration: 10000, icon: 'ℹ️' });
      } else {
        const pieces = [];
        if (r.time_off_approved_added) pieces.push(`${r.time_off_approved_added} approved`);
        if (r.time_off_pending_added) pieces.push(`${r.time_off_pending_added} pending`);
        toast.success(`Blocked out ${total} day${total === 1 ? '' : 's'} (${pieces.join(' + ')}). ${foundLine}`, { duration: 10000 });
      }
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Sync failed'); }
  };

  // Realign classroom colors on this week's shifts to what each staff's
  // Weekly Template says. Fixes the case where cells were populated
  // (copy last week / manual add) BEFORE the template existed, so the
  // shift times are fine but the classroom is stale or blank.
  const syncClassrooms = async () => {
    try {
      const { data: r } = await api.post('/team-schedule/sync-classrooms-from-templates', {
        week_start: formatISO(weekStart),
      });
      if (r.cells_updated > 0) {
        toast.success(`Fixed ${r.cells_updated} shift${r.cells_updated === 1 ? '' : 's'} to match their template classroom.`);
      } else {
        toast(`Every shift with a template already matches. (${r.cells_already_correct} correct, ${r.cells_without_template} have no template.)`, { icon: 'ℹ️', duration: 8000 });
      }
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Sync failed'); }
  };

  // Seed the week from each staff's weekly template (set on the staff's
  // Attendance tab). Approved time-off survives because those days
  // already have an override and we skip-not-overwrite.
  const seedFromTemplates = async () => {
    if (!window.confirm(`Fill this week from each staff's weekly template? Approved AND pending time-off requests get blocked out first — templates fill in around them. Existing cells won't be overwritten.`)) return;
    try {
      const { data: r } = await api.post('/team-schedule/seed-from-templates', {
        week_start: formatISO(weekStart),
      });
      if (r.template_rows_considered === 0) {
        toast.error("No weekly templates set yet. Add them on each staff's Attendance tab.");
        return;
      }
      const parts = [`Added ${r.shifts_inserted} shift${r.shifts_inserted === 1 ? '' : 's'} from templates`];
      const timeOffAdded = (r.time_off_approved_added || 0) + (r.time_off_pending_added || 0);
      if (timeOffAdded > 0) {
        const pieces = [];
        if (r.time_off_approved_added) pieces.push(`${r.time_off_approved_added} approved`);
        if (r.time_off_pending_added) pieces.push(`${r.time_off_pending_added} pending`);
        parts.push(`blocked out ${timeOffAdded} time-off day${timeOffAdded === 1 ? '' : 's'} (${pieces.join(' + ')})`);
      }
      if (r.shifts_skipped) parts.push(`${r.shifts_skipped} skipped`);
      toast.success(parts.join(' · '));
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Seed failed'); }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const monthLabel = weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const coverageAlerts = data.lunch_coverage_alerts || [];
  // Publication lock: published weeks are read-only for admins. The
  // backend has the final word (every mutation re-checks) — this just
  // hides the buttons so the UI never invites a denied click.
  const canEdit = data.viewer_can_edit !== false;
  const isPublished = !!data.published_at;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronLeftIcon className="w-4 h-4" /></button>
          <p className="text-sm font-semibold text-gray-900 min-w-[200px] text-center">
            Week of {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {monthLabel}
          </p>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronRightIcon className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {canEdit && (
            <button onClick={() => setPickerOpen(true)}
              title="Add a single shift for a staff member on any day this week."
              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg">
              <PlusIcon className="w-3.5 h-3.5" /> Add shift
            </button>
          )}
          <button onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="text-xs text-gray-500 hover:text-orange-500 px-2 py-1">This week</button>
          <button onClick={openEmailPreview}
            title={isPublished
              ? 'Re-publish this week. Edited shifts will trigger change emails to affected staff.'
              : 'Preview which staff will receive the weekly schedule email, then send. After sending, the week locks for admins.'}
            className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 px-2 py-1 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100">
            <EnvelopeIcon className="w-3.5 h-3.5" />
            {isPublished ? 'Re-publish & email' : 'Send weekly emails…'}
          </button>
          {canEdit && (
            <>
              <button onClick={seedFromTemplates}
                title="Fill this week from each staff's weekly template (set on their Attendance tab). Approved time-off stays as-is."
                className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-800 px-2 py-1 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100">
                <SparklesIcon className="w-3.5 h-3.5" /> Use templates
              </button>
              <button onClick={copyFromLastWeek}
                title="Copy last week's shifts forward — skipping any cell already filled (Off requests, manual entries)."
                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100">
                <ArrowUturnLeftIcon className="w-3.5 h-3.5" /> Copy last week
              </button>
              <button onClick={syncTimeOff}
                title="Re-check approved + pending time-off requests for this week. Approved days overwrite any typed shifts."
                className="inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-900 px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100">
                <CalendarDaysIcon className="w-3.5 h-3.5" /> Sync time off
              </button>
              <button onClick={syncClassrooms}
                title="Realign every shift's classroom color to what each staff's Weekly Template says. Fixes stale rooms from Copy last week or manual entries."
                className="inline-flex items-center gap-1 text-xs text-purple-800 hover:text-purple-900 px-2 py-1 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100">
                <SparklesIcon className="w-3.5 h-3.5" /> Sync rooms
              </button>
              {!isPublished && (
                <button onClick={clearWeek}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg border border-red-200 hover:bg-red-50">
                  <TrashIcon className="w-3.5 h-3.5" /> Clear this week
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isPublished && (
        <div className={`mb-3 px-4 py-2 rounded-xl border flex items-center gap-2 text-sm ${
          canEdit ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'
        }`}>
          <LockClosedIcon className="w-4 h-4 flex-shrink-0" />
          <p>
            <strong>Schedule published</strong>{' '}
            {new Date(data.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {data.republished_count > 0 && ` (republished ${data.republished_count}×)`}.
            {canEdit
              ? ' Edits now trigger a "schedule changed" email to the affected staff. Whole-week "Clear" is disabled — you can only edit individual shifts.'
              : ' This week is locked. Only the director can adjust it now.'}
          </p>
        </div>
      )}

      {Array.isArray(data.time_off_this_week) && data.time_off_this_week.length > 0 && (
        <TimeOffThisWeekBanner items={data.time_off_this_week} />
      )}

      {coverageAlerts.length > 0 && <LunchCoverageBanner alerts={coverageAlerts} />}

      <p className="text-xs text-gray-500 mb-2">
        Click <strong className="text-orange-600">+ Add shift</strong> above, or tap any cell to edit it. Tap a <strong className="text-gray-600">+ Add</strong> cell to fill an Off day.{' '}
        <span className="hidden md:inline">⌘/Ctrl-click cells to multi-select, then apply one shift to all of them at once.</span>
      </p>

      {selection.size > 0 && (
        <BulkSelectionBar
          count={selection.size}
          onApply={() => setBulkOpen(true)}
          onClear={clearSelection}
        />
      )}

      <RightNowPanel weekStart={weekStart} rows={data.rows} classrooms={classrooms} />

      <FlippedWeekGrid
        weekStart={weekStart}
        rows={data.rows}
        classrooms={classrooms}
        onToggleEarly={toggleEarly}
        onEditCell={canEdit ? openEdit : undefined}
        selection={selection}
        onToggleSelect={canEdit ? toggleSelection : undefined}
        onMarkWeekOff={canEdit ? markStaffWeekOff : undefined}
      />

      <CoverageHeatmap weekStart={weekStart} rows={data.rows} classrooms={classrooms} />

      <ClassroomLegend classrooms={classrooms} />

      {editing && (
        <EditShiftModal
          staff={editing.staff}
          date={editing.date}
          cell={editing.cell}
          classrooms={classrooms}
          onCancel={closeEdit}
          onSave={saveEdit}
          onDelete={deleteShift}
        />
      )}
      {bulkOpen && (
        <BulkApplyModal
          count={selection.size}
          classrooms={classrooms}
          onCancel={() => setBulkOpen(false)}
          onApply={applyBulk}
        />
      )}
      {emailPreview && (
        <EmailPreviewModal
          preview={emailPreview}
          onCancel={() => setEmailPreview(null)}
          onSend={sendEmails}
        />
      )}
      {pickerOpen && (
        <AddShiftPicker
          weekStart={weekStart}
          rows={data.rows}
          onCancel={() => setPickerOpen(false)}
          onPick={(staff, dateISO) => {
            setPickerOpen(false);
            // Find the existing cell for that staff/day so the modal
            // can pre-fill if there's already something there.
            const existingCell = staff.cells?.find(c => c.date === dateISO) || null;
            openEdit(staff, dateISO, existingCell && existingCell.status === 'working' ? existingCell : null);
          }}
        />
      )}
    </div>
  );
}

// "Who?" + "Which day?" picker that opens the EditShiftModal. Built so
// admins don't have to know cells are clickable to add a shift.
function AddShiftPicker({ weekStart, rows, onCancel, onPick }) {
  const [staffId, setStaffId] = useState('');
  const [dayIdx, setDayIdx] = useState(0);
  const sorted = [...rows].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || '')
  );
  const days = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const submit = () => {
    if (!staffId) return toast.error('Pick a staff member.');
    const staff = sorted.find(s => s.id === Number(staffId));
    if (!staff) return;
    onPick(staff, formatISO(days[dayIdx]));
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <PlusIcon className="w-5 h-5 text-orange-500" />
              Add a shift
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Pick who and when, then set the times.</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Staff member</label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} autoFocus
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
              <option value="">— Pick someone —</option>
              {sorted.map(s => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name}{s.role ? ` · ${s.role.replace(/_/g, ' ')}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Day</label>
            <div className="grid grid-cols-5 gap-1">
              {days.map((d, i) => (
                <button key={i} onClick={() => setDayIdx(i)}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium ${dayIdx === i ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  <div>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className="text-[10px] opacity-80">{d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-gray-100">
          <button onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={submit}
            className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600">
            Next: set times →
          </button>
        </div>
      </div>
    </div>
  );
}

// Preview modal: lists every active scheduled staff member with their
// shift count + email-on-file. Director can untick anyone before
// firing. "No email on file" rows are auto-deselected and disabled.
function EmailPreviewModal({ preview, onCancel, onSend }) {
  const eligible = preview.staff.filter(s => s.email && s.shift_count > 0);
  const [selected, setSelected] = useState(new Set(eligible.map(s => s.staff_id)));
  const [sending, setSending] = useState(false);
  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allOn = selected.size === eligible.length;
  const flip = () => setSelected(allOn ? new Set() : new Set(eligible.map(s => s.staff_id)));
  const send = async () => {
    setSending(true);
    try { await onSend([...selected]); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <EnvelopeIcon className="w-5 h-5 text-green-600" />
              Send weekly schedule emails
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Week of {preview.week_start_label}. Each recipient gets a short "your schedule is posted" announcement with a sign-in link.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-2">
          <button onClick={flip}
            className="text-xs text-orange-600 hover:text-orange-700">
            {allOn ? 'Deselect all' : 'Select all'}
          </button>
          <span className="text-xs text-gray-500">{selected.size} of {eligible.length} selected</span>
        </div>

        <ul className="space-y-1 mb-4">
          {preview.staff.map(s => {
            const noEmail = !s.email;
            const noShifts = s.shift_count === 0;
            const blocked = noEmail || noShifts;
            const checked = selected.has(s.staff_id);
            return (
              <li key={s.staff_id}
                className={`flex items-center justify-between text-sm px-2 py-1.5 rounded ${blocked ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'}`}>
                <label className="flex items-center gap-2 flex-1 min-w-0">
                  <input type="checkbox"
                    checked={checked && !blocked} disabled={blocked}
                    onChange={() => !blocked && toggle(s.staff_id)}
                    className="accent-green-500" />
                  <span className="truncate">{s.first_name} {s.last_name}</span>
                </label>
                <div className="flex items-center gap-2 text-[11px]">
                  {noShifts && <span className="text-gray-400">no shifts</span>}
                  {noEmail && <span className="text-amber-600">no email</span>}
                  {!blocked && (
                    <span className="text-gray-500">{s.shift_count} shift{s.shift_count === 1 ? '' : 's'}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-[11px] text-gray-400 max-w-[55%]">
            Anyone without an email or with no scheduled shifts is skipped. Update their profile or set shifts first.
          </p>
          <div className="flex gap-2">
            <button onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={send} disabled={sending || selected.size === 0}
              className="text-sm px-4 py-1.5 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">
              {sending ? 'Sending…' : `Send to ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sticky action bar that shows when 1+ cells are selected.
function BulkSelectionBar({ count, onApply, onClear }) {
  return (
    <div className="sticky top-14 z-20 mb-3 flex items-center justify-between gap-3 px-4 py-2 rounded-xl border border-orange-300 bg-orange-50 shadow-sm">
      <p className="text-sm font-medium text-orange-800">
        {count} cell{count === 1 ? '' : 's'} selected
      </p>
      <div className="flex items-center gap-2">
        <button onClick={onClear}
          className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded">
          Clear
        </button>
        <button onClick={onApply}
          className="text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          <PencilSquareIcon className="w-3.5 h-3.5" /> Apply shift to all
        </button>
      </div>
    </div>
  );
}

// Mini-modal that captures one shift's start/end/classroom/lunch and
// applies it to every cell in the current selection.
function BulkApplyModal({ count, classrooms, onCancel, onApply }) {
  const [start, setStart] = useState('07:00');
  const [end, setEnd] = useState('17:00');
  const [classroom, setClassroom] = useState('');
  const [lunch, setLunch] = useState('');
  const [saving, setSaving] = useState(false);
  const sortedClassrooms = [...(classrooms || [])].sort((a, b) => a.display_order - b.display_order);

  const submit = async (e) => {
    e.preventDefault();
    if (!start || !end) return toast.error('Start and end time are required.');
    setSaving(true);
    try {
      await onApply({ start_time: start, end_time: end,
        classroom: classroom || null, lunch_break: lunch || null });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <PencilSquareIcon className="w-5 h-5 text-orange-500" />
              Apply shift to {count} cell{count === 1 ? '' : 's'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              All selected cells get the same start, end, classroom, and lunch. Existing shifts are overwritten.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Start</label>
              <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">End</label>
              <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Lunch (optional)</label>
            <input value={lunch} onChange={(e) => setLunch(e.target.value)}
              placeholder="e.g. 12-1 or leave blank"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Classroom</label>
            <select value={classroom} onChange={(e) => setClassroom(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              <option value="">— Keep each staff's primary —</option>
              {sortedClassrooms.map(c => (
                <option key={c.id} value={c.name}>
                  {c.name}{c.max_kids_per_staff < 50 ? ` (1:${c.max_kids_per_staff})` : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">
              Leave blank to use each staff's primary classroom. Pick a room here to override everyone.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Applying…' : `Apply to ${count}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Days-as-rows × staff-as-columns. Each day takes N visual rows; N is
// driven by whoever has the most segments that day (min 3 = start/lunch/end).
//
// Cell rendering:
//  - status off/off_request/call_off/sick/holiday → single block, label
//    spans all N rows of the day for that staff (rowSpan).
//  - working + no segments → 3 visual rows (start / lunch / end), each
//    colored by the single classroom.
//  - working + segments → one row per segment, colored by that segment's
//    classroom. Matches BBC's printed schedule where a teacher who
//    covers PreK at lunch has the middle row in PreK purple.
function FlippedWeekGrid({ weekStart, rows, classrooms, onToggleEarly, onEditCell, selection, onToggleSelect, onMarkWeekOff }) {
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const cellFor = (staffRow, dayISO) => staffRow.cells?.find(c => c.date === dayISO);

  // Toggle: hide staff with no working shifts this week. PRN/float
  // staff marked off, center-closed full weeks, etc. fall away so the
  // grid stays readable. Re-show with the button below the grid.
  const [showHidden, setShowHidden] = useState(false);

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        No active staff to schedule yet.
      </div>
    );
  }

  const hasAnyWorking = (staff) =>
    (staff.cells || []).some(c => c.status === 'working' && c.start_time);
  const hiddenCount = rows.filter(s => !hasAnyWorking(s)).length;

  // Stable column order: by classroom (display_order from /api/classrooms),
  // then first name. Tries hard to figure out where each staff belongs
  // even when primary_classroom isn't set yet, by looking at where
  // they're actually scheduled this week.
  const classroomOrder = (s) => {
    if (s.primary_classroom) {
      const c = classrooms.find(c => c.name === s.primary_classroom);
      if (c) return c.display_order;
    }
    // Role-based fallback for staff that don't belong to a specific
    // class regardless of cells.
    if (s.role === 'admin' || s.role === 'director') {
      const adminClassroom = classrooms.find(c => c.name === 'Admin');
      return adminClassroom ? adminClassroom.display_order : -1;
    }
    if (s.role === 'float') {
      const floatRoom = classrooms.find(c => c.name === 'Float' || c.name === 'Floats');
      return floatRoom ? floatRoom.display_order : 800;
    }
    // Teacher/cook without an explicit primary_classroom: infer from
    // the most common classroom across their working cells this week.
    // Lands Tijahna with Babies-side staff, Tommisha with PreK, etc.
    const counts = {};
    for (const cell of (s.cells || [])) {
      if (cell.status !== 'working') continue;
      if (Array.isArray(cell.segments) && cell.segments.length > 0) {
        for (const seg of cell.segments) {
          if (seg.classroom) counts[seg.classroom] = (counts[seg.classroom] || 0) + 1;
        }
      } else if (cell.classroom) {
        counts[cell.classroom] = (counts[cell.classroom] || 0) + 1;
      }
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (top) {
      const c = classrooms.find(c => c.name === top);
      if (c) return c.display_order;
    }
    return 999;
  };
  const sortedRows = [...rows].sort((a, b) => {
    const oa = classroomOrder(a);
    const ob = classroomOrder(b);
    if (oa !== ob) return oa - ob;
    return (a.first_name || '').localeCompare(b.first_name || '');
  });

  // The visible columns: by default skip anyone with no working shifts.
  const staffCols = showHidden ? sortedRows : sortedRows.filter(hasAnyWorking);

  // All scheduled staff hidden? Render a friendly empty state with the
  // toggle so the director can recover instead of staring at a blank
  // table skeleton.
  if (staffCols.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-600 font-medium">No one is scheduled for this week yet.</p>
        <p className="text-xs text-gray-500 mt-1">
          Use <strong>+ Add shift</strong> or <strong>Use templates</strong> to build the week.
        </p>
        {hiddenCount > 0 && (
          <button onClick={() => setShowHidden(true)}
            className="mt-3 text-xs text-orange-600 hover:text-orange-700 font-medium">
            Show all {hiddenCount} staff
          </button>
        )}
      </div>
    );
  }

  // How many vertical slots one staff/day cell needs.
  const slotsFor = (cell) => {
    if (!cell) return 1;
    if (cell.status !== 'working') return 1; // off / off_request / call_off / sick / holiday
    if (Array.isArray(cell.segments) && cell.segments.length > 0) return cell.segments.length;
    return 3; // single-shift fallback (start / lunch / end)
  };

  // Sum scheduled hours for one staff across all visible days. Segments
  // sum per-segment; single shifts subtract lunch when one is logged.
  const hoursForStaff = (staffRow) => {
    let mins = 0;
    for (const cell of staffRow.cells || []) {
      if (cell.status !== 'working') continue;
      if (Array.isArray(cell.segments) && cell.segments.length > 0) {
        for (const seg of cell.segments) mins += diffMinutes(seg.start, seg.end);
      } else if (cell.start_time && cell.end_time) {
        mins += diffMinutes(cell.start_time, cell.end_time);
        mins -= lunchMinutes(cell.lunch_break);
      }
    }
    return Math.max(0, Math.round(mins / 6) / 10); // hours, 1 decimal
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide sticky left-0 bg-gray-50">
              Day
            </th>
            {staffCols.map(s => {
              const hrs = hoursForStaff(s);
              const target = s.target_weekly_hours;
              // Off-target = 5+ hrs deviation. Tighter than that creates
              // too much noise once you account for legit short weeks.
              let badgeCls = 'text-gray-400';
              let title = `${hrs} hr scheduled`;
              if (target) {
                const delta = hrs - target;
                title = `${hrs} of ${target} target hr (${delta >= 0 ? '+' : ''}${delta.toFixed(1)})`;
                if (delta >= 5)      badgeCls = 'text-red-600 font-semibold';   // overscheduled
                else if (delta >= 2) badgeCls = 'text-amber-600 font-semibold'; // mild over
                else if (delta <= -5) badgeCls = 'text-red-600 font-semibold';   // underscheduled
                else if (delta <= -2) badgeCls = 'text-amber-600 font-semibold'; // mild under
                else                  badgeCls = 'text-green-600';                // on target
              }
              // Show a small pipeline badge above the hours so admins
              // can see at a glance which columns are onboarding or
              // pre-clearance staff (still scheduled for training).
              const showPipelineBadge = s.pipeline_stage && s.pipeline_stage.id !== 'active';
              return (
                <th key={s.id} className="px-2 py-2 text-center text-[11px] font-semibold text-gray-700 border-l border-gray-100 min-w-[90px]">
                  <StaffLink id={s.id} name={s.first_name} />
                  <div className="text-[10px] font-normal text-gray-400 mt-0.5 truncate" title={s.role?.replace(/_/g, ' ')}>
                    {s.role?.replace(/_/g, ' ') || ''}
                  </div>
                  {showPipelineBadge && (
                    <div className="mt-0.5">
                      <PipelineStage stage={s.pipeline_stage} size="sm" />
                    </div>
                  )}
                  <div className={`text-[10px] mt-0.5 ${badgeCls}`} title={title}>
                    {hrs} hr
                  </div>
                  {onMarkWeekOff && (
                    <button onClick={() => onMarkWeekOff(s)}
                      title={`Mark ${s.first_name} off for this whole week (Mon–Fri).`}
                      className="mt-1 text-[9px] text-gray-400 hover:text-red-600 underline-offset-2 hover:underline">
                      ✕ Off this week
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {weekDays.map(day => {
            const dayISO = formatISO(day);
            const dayLabel = day.toLocaleDateString('en-US', { weekday: 'long' });
            const dateLabel = day.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

            const cellsByStaff = staffCols.map(s => cellFor(s, dayISO));
            const dayRowCount = Math.max(3, ...cellsByStaff.map(slotsFor));

            const visualRows = [];
            for (let rowIdx = 0; rowIdx < dayRowCount; rowIdx++) {
              const tdsForRow = [];
              staffCols.forEach((s, i) => {
                const cell = cellsByStaff[i];
                const slots = slotsFor(cell);
                // Status cells (Off / CO / etc.) rowSpan the whole day —
                // only emit on row 0, skip on later rows.
                const isStatusCell = !cell || cell.status !== 'working';
                const selected = selection?.has(`${s.id}|${dayISO}`);
                if (isStatusCell) {
                  if (rowIdx === 0) {
                    tdsForRow.push(renderStatusCell({ s, cell, dayISO, dayLabel, dayRowCount, classrooms, onEditCell, onToggleSelect, selected }));
                  }
                  return;
                }
                // Working cell, may span fewer rows than dayRowCount.
                // If beyond this staff's slot count, emit a blank filler.
                if (rowIdx >= slots) {
                  tdsForRow.push(
                    <td key={s.id} className="bg-white border-l border-white" />
                  );
                  return;
                }
                tdsForRow.push(renderWorkingSubCell({
                  s, cell, rowIdx, slots, dayISO, dayLabel,
                  classrooms, onEditCell, onToggleEarly,
                  onToggleSelect, selected,
                }));
              });

              // Heavy dark border between days so the eye doesn't blur
              // the end of one day into the start of the next. Per
              // director (2026-06-04). First row of each day gets the
              // thick top border.
              const dayBoundaryClass = rowIdx === 0 ? 'border-t-4 border-gray-900' : '';
              visualRows.push(
                <tr key={`${dayISO}-${rowIdx}`} className={dayBoundaryClass}>
                  {rowIdx === 0 && (
                    <td rowSpan={dayRowCount}
                      className="px-3 py-2 align-top text-left border-r-2 border-gray-900 sticky left-0 bg-white">
                      <div className="text-sm font-semibold text-gray-900">{dayLabel}</div>
                      <div className="text-[11px] text-gray-400">{dateLabel}</div>
                    </td>
                  )}
                  {tdsForRow}
                </tr>
              );
            }
            return visualRows;
          })}
        </tbody>
      </table>
      {hiddenCount > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-xs">
          <span className="text-gray-500">
            {showHidden
              ? `Showing all ${rows.length} staff.`
              : `${hiddenCount} staff hidden (no shifts this week).`}
          </span>
          <button onClick={() => setShowHidden(v => !v)}
            className="text-orange-600 hover:text-orange-700 font-medium">
            {showHidden ? 'Hide unscheduled' : `Show all (${hiddenCount} hidden)`}
          </button>
        </div>
      )}
    </div>
  );
}

// Off / Off request / CO / Sick / Holiday — one big cell spanning the
// day's whole row count for that staff. Clickable so admins can change
// the status or add a shift.
function renderStatusCell({ s, cell, dayISO, dayLabel, dayRowCount, classrooms, onEditCell, onToggleSelect, selected }) {
  const status = cell?.status || 'off';
  const STATUS_META = {
    off:                { label: '+ Add',        bg: 'bg-gray-50 hover:bg-orange-50', fg: 'text-gray-400 hover:text-orange-600' },
    off_request:        { label: 'Off request',  bg: 'bg-white',      fg: 'text-gray-600' }, // legacy
    time_off_approved:  { label: 'Off request',  bg: 'bg-white',      fg: 'text-gray-600' },
    time_off_pending:   { label: 'Off · pending',bg: 'bg-amber-50',   fg: 'text-amber-700 font-semibold' },
    call_off:           { label: 'CO',           bg: 'bg-white',      fg: 'text-red-600 font-bold' },
    sick:               { label: 'Sick',         bg: 'bg-rose-50',    fg: 'text-rose-700' },
    holiday:            { label: 'Holiday',      bg: 'bg-violet-50',  fg: 'text-violet-700' },
    center_closed:      { label: cell?.closure_name || 'CLOSED',
                          bg: 'bg-violet-100', fg: 'text-violet-800 font-semibold' },
  };
  const meta = STATUS_META[status] || STATUS_META.off;
  // Modifier-click → toggle multi-select; plain click → open edit modal.
  // Always pass the cell through to the modal so a CO cell opens with
  // status='call_off' pre-selected.
  const onClick = (e) => {
    if ((e.metaKey || e.ctrlKey || e.shiftKey) && onToggleSelect) {
      e.preventDefault();
      onToggleSelect(s, dayISO);
      return;
    }
    onEditCell && onEditCell(s, dayISO, cell || null);
  };
  const cursorClass = onEditCell ? 'cursor-pointer hover:ring-2 hover:ring-orange-300' : '';
  const selectedRing = selected ? 'ring-2 ring-orange-500 ring-inset' : '';
  const partialOff = Array.isArray(cell?.partial_time_off) ? cell.partial_time_off : null;
  return (
    <td key={s.id} rowSpan={dayRowCount}
      onClick={onClick}
      title={onEditCell ? `${s.first_name} · ${dayLabel} — click to edit, ⌘-click to multi-select` : undefined}
      className={`text-center text-xs border-l border-white align-middle ${meta.bg} ${meta.fg} ${cursorClass} ${selectedRing}`}>
      <div>{meta.label}</div>
      {partialOff && partialOff.map((p, i) => (
        <div key={i}
          title={p.reason || 'Partial time-off window'}
          className={`mt-1 text-[10px] font-semibold ${p.status === 'approved' ? 'text-amber-800' : 'text-amber-600'}`}>
          ⚠ Off {formatTime(p.start)}–{formatTime(p.end)}
        </div>
      ))}
    </td>
  );
}

// One visual sub-row for a working cell. For segmented shifts: shows the
// segment's "start–end" with that segment's classroom color. For single
// shifts: row 0 = start time, row 1 = lunch line, row 2 = end time, all
// colored by the single classroom.
function renderWorkingSubCell({ s, cell, rowIdx, slots, dayISO, dayLabel, classrooms, onEditCell, onToggleEarly, onToggleSelect, selected }) {
  const hasSegments = Array.isArray(cell.segments) && cell.segments.length > 0;
  const isLastRow = rowIdx === slots - 1;

  let inner = null;
  let segClassroom = cell.classroom;
  // Inverted = end time before start time. Almost always am/pm typo. Flag
  // both the cell (red ring) and the end-time row (warning icon) so the
  // director sees exactly which shift to fix.
  const cellInverted = hasSegments
    ? cell.segments.some(seg => isInverted(seg.start, seg.end))
    : isInverted(cell.start_time, cell.end_time);

  if (hasSegments) {
    const seg = cell.segments[rowIdx];
    segClassroom = seg.classroom || cell.classroom;
    const segInverted = isInverted(seg.start, seg.end);
    inner = (
      <span className={segInverted ? 'text-red-700 font-semibold' : ''}>
        {formatTime(seg.start)}{seg.end ? `–${formatTime(seg.end)}` : ''}
        {segInverted && <ExclamationTriangleIcon className="inline w-3 h-3 ml-0.5" />}
      </span>
    );
  } else {
    // Single-shift: 3 rows (start / lunch / end). If dayRowCount > 3, the
    // staff is in their first 3 slots — the rest are blank fillers
    // (handled in the parent).
    if (rowIdx === 0) {
      inner = <span>{formatTime(cell.start_time)}</span>;
    } else if (rowIdx === 1) {
      // Lunch row. Optional — only show "needs coverage" warning when
      // the policy flags it; otherwise show the lunch range or em-dash.
      if (cell.needs_lunch_coverage) {
        inner = (
          <span className="inline-flex items-center gap-0.5 text-amber-700 font-medium" title="Needs lunch coverage">
            <ExclamationTriangleIcon className="w-3 h-3" /> need
          </span>
        );
      } else {
        inner = <span>{cell.lunch_break || '—'}</span>;
      }
    } else if (rowIdx === 2) {
      inner = (
        <span className={cellInverted ? 'inline-flex items-center gap-0.5 text-red-700 font-semibold' : ''}
          title={cellInverted ? 'End time is before start time — likely am/pm typo. Click to fix.' : undefined}>
          {formatTime(cell.end_time)}
          {cellInverted && <ExclamationTriangleIcon className="w-3 h-3" />}
        </span>
      );
    }
  }

  // Lunch row on a non-segmented shift renders white (it's a break,
  // not a working block); everything else takes the classroom color.
  const isLunchRow = !hasSegments && rowIdx === 1;
  const theme = isLunchRow ? FALLBACK_THEME : themeForRoom(segClassroom, classrooms);
  const bg = isLunchRow ? 'bg-white text-gray-600' : `${theme.head} text-gray-800`;
  const invertedRing = cellInverted ? 'ring-1 ring-red-400 ring-inset' : '';

  const onClick = (e) => {
    if ((e.metaKey || e.ctrlKey || e.shiftKey) && onToggleSelect) {
      e.preventDefault();
      onToggleSelect(s, dayISO);
      return;
    }
    onEditCell && onEditCell(s, dayISO, cell);
  };
  const cursorClass = onEditCell ? 'cursor-pointer hover:ring-2 hover:ring-orange-300' : '';
  const selectedRing = selected ? 'ring-2 ring-orange-500 ring-inset' : '';

  // Early-clockin lock lives on the last row of an override shift.
  const showEarlyToggle = isLastRow && cell.source === 'override';

  return (
    <td key={s.id}
      onClick={onClick}
      title={onEditCell ? `${s.first_name} · ${dayLabel} — click to edit, ⌘-click to multi-select` : undefined}
      className={`px-1 py-1 text-center text-xs border-l border-white ${cursorClass} ${bg} ${selectedRing} ${invertedRing}`}>
      <div className="flex flex-col items-center gap-0.5">
        {inner}
        {isLastRow && Array.isArray(cell.partial_time_off) && cell.partial_time_off.map((p, i) => (
          <span key={i}
            title={p.reason || 'Partial time-off window'}
            className={`text-[9px] font-semibold ${p.status === 'approved' ? 'text-amber-800' : 'text-amber-600'}`}>
            ⚠ Off {formatTime(p.start)}–{formatTime(p.end)}
          </span>
        ))}
        {isLastRow && segClassroom && !hasSegments && (
          <span className="text-[9px] text-gray-500 truncate max-w-full" title={segClassroom}>
            {segClassroom}
          </span>
        )}
        {showEarlyToggle && (
          <button onClick={(e) => { e.stopPropagation(); onToggleEarly(cell.override_id, cell.early_clockin_allowed); }}
            title={cell.early_clockin_allowed ? 'Click to revoke early clock-in' : 'Click to allow early clock-in'}
            className="inline-flex items-center text-[9px] text-gray-500 hover:text-orange-600">
            {cell.early_clockin_allowed
              ? <LockOpenIcon className="w-3 h-3 text-green-600" />
              : <LockClosedIcon className="w-3 h-3" />}
          </button>
        )}
      </div>
    </td>
  );
}

// Returns the list of staff whose shift covers `hour` (0-23) in
// `classroomName` on `dayISO`. Walks segments when present; otherwise
// the single (start_time, end_time) on the cell. Used by both insight
// panels — the "right now" view and the coverage heatmap.
function staffInRoomAtHour(rows, dayISO, classroomName, hour) {
  const matched = [];
  const targetMins = hour * 60;
  const covers = (start, end) => {
    if (!start || !end) return false;
    const [sh, sm] = String(start).split(':').map(Number);
    const [eh, em] = String(end).split(':').map(Number);
    if ([sh, sm, eh, em].some(Number.isNaN)) return false;
    const startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins <= startMins) endMins += 24 * 60;
    return startMins <= targetMins && targetMins < endMins;
  };
  for (const staff of rows || []) {
    const cell = staff.cells?.find(c => c.date === dayISO);
    if (!cell || cell.status !== 'working') continue;
    if (Array.isArray(cell.segments) && cell.segments.length > 0) {
      for (const seg of cell.segments) {
        if ((seg.classroom || cell.classroom) === classroomName && covers(seg.start, seg.end)) {
          matched.push(staff);
          break;
        }
      }
    } else if (cell.classroom === classroomName) {
      if (covers(cell.start_time, cell.end_time)) matched.push(staff);
    }
  }
  return matched;
}

// "Right now" panel — only renders when the current calendar day is
// inside the visible Mon–Fri window. Shows which rooms have which staff
// at the current hour. Quick reality check during the day.
function RightNowPanel({ weekStart, rows, classrooms }) {
  const now = new Date();
  const todayISO = formatISO(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  // Skip render if today isn't Mon–Fri of the visible week.
  const fri = addDays(weekStart, 4);
  if (now < weekStart || now > new Date(fri.getFullYear(), fri.getMonth(), fri.getDate(), 23, 59)) return null;
  if (now.getDay() === 0 || now.getDay() === 6) return null;

  const hour = now.getHours();
  const hourLabel = formatTime(`${String(hour).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
  const sortedRooms = [...classrooms].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-900">
          Right now — {hourLabel}
        </p>
        <p className="text-[11px] text-gray-400">
          {now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {sortedRooms.map(room => {
          const staff = staffInRoomAtHour(rows, todayISO, room.name, hour);
          const theme = themeForRoom(room.name, classrooms);
          const empty = staff.length === 0;
          return (
            <div key={room.id}
              className={`rounded-xl border px-3 py-2 text-xs min-w-[140px] ${empty ? 'bg-gray-50 border-gray-200' : theme.head}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`font-semibold ${empty ? 'text-gray-400' : 'text-gray-800'}`}>{room.name}</span>
                <span className={`text-[10px] font-semibold ${empty ? 'text-gray-400' : 'text-gray-600'}`}>
                  {staff.length}
                </span>
              </div>
              {empty ? (
                <p className="text-[11px] text-gray-400">— no one assigned</p>
              ) : (
                <p className="text-[11px] text-gray-700">
                  {staff.map(s => s.first_name).join(', ')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Coverage heatmap — classroom rows × hour columns for one selected day,
// each cell shows the number of staff scheduled in that room at that
// hour. Helps catch "PreK has no one at 11am" gaps. Days tab selectable
// at the top.
function CoverageHeatmap({ weekStart, rows, classrooms }) {
  const days = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const todayISO = formatISO(new Date());
  // Default to today if visible, else Monday.
  const initialIdx = Math.max(0, days.findIndex(d => formatISO(d) === todayISO));
  const [dayIdx, setDayIdx] = useState(initialIdx);
  useEffect(() => { setDayIdx(Math.max(0, initialIdx)); }, [initialIdx]);

  const dayISO = formatISO(days[dayIdx] || days[0]);
  const sortedRooms = [...classrooms].sort((a, b) => a.display_order - b.display_order);
  const hours = []; for (let h = 6; h <= 19; h++) hours.push(h); // 6am–7pm

  return (
    <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm font-semibold text-gray-900">Coverage by hour</p>
        <div className="flex gap-1">
          {days.map((d, i) => (
            <button key={i} onClick={() => setDayIdx(i)}
              className={`px-2.5 py-1 text-xs font-medium rounded-lg ${dayIdx === i ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {d.toLocaleDateString('en-US', { weekday: 'short' })}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="text-left px-2 py-1 text-[10px] font-semibold uppercase text-gray-500">Room</th>
              {hours.map(h => (
                <th key={h} className="px-1 py-1 text-[10px] font-medium text-gray-500 text-center">
                  {h % 12 || 12}{h < 12 ? 'a' : 'p'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRooms.map(room => {
              const theme = themeForRoom(room.name, classrooms);
              return (
                <tr key={room.id}>
                  <td className={`px-2 py-1 text-[11px] font-semibold ${theme.head}`}>{room.name}</td>
                  {hours.map(h => {
                    const staff = staffInRoomAtHour(rows, dayISO, room.name, h);
                    const n = staff.length;
                    const tooltip = n > 0
                      ? `${room.name} at ${formatTime(`${String(h).padStart(2, '0')}:00`)}: ${staff.map(s => s.first_name).join(', ')}`
                      : `${room.name} at ${formatTime(`${String(h).padStart(2, '0')}:00`)}: no staff`;
                    const cellBg = n === 0 ? 'bg-gray-50 text-gray-300'
                      : n === 1 ? 'bg-orange-50 text-orange-800'
                      : 'bg-green-50 text-green-800 font-semibold';
                    return (
                      <td key={h} title={tooltip}
                        className={`text-center px-1 py-1 border-l border-white ${cellBg}`}>
                        {n}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-400 mt-2">
        Numbers count working segments overlapping each hour. Hover for who's there.
      </p>
    </div>
  );
}

// Color key for the grid above. Mirrors the classroom palette configured
// under Classrooms & Ratios so admins can match cells to rooms at a glance.
function ClassroomLegend({ classrooms }) {
  if (!classrooms || classrooms.length === 0) return null;
  const sorted = [...classrooms].sort((a, b) => a.display_order - b.display_order);
  return (
    <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-4">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Classroom colors</p>
      <div className="flex flex-wrap gap-2">
        {sorted.map(c => {
          const theme = themeForRoom(c.name, classrooms);
          return (
            <span key={c.id}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${theme.head}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${theme.bar}`} />
              <span className="font-medium text-gray-800">{c.name}</span>
              {c.max_kids_per_staff < 50 && (
                <span className="text-[10px] text-gray-500">1:{c.max_kids_per_staff}</span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Inline shift editor. Two modes:
//
//   - "Single shift" → one classroom + start/lunch/end, like before.
//   - "Split into segments" → up to 4 contiguous (start/end/classroom)
//     blocks, for days where the teacher covers another room mid-day.
//
// Status is what the cell becomes when not "working" — Off, Time off
// (approved), Call off, Sick, Holiday. Lunch is fully optional; the
// 7+ hr coverage flag still fires automatically when applicable.
function EditShiftModal({ staff, date, cell, classrooms, onCancel, onSave, onDelete }) {
  const initialSegments = Array.isArray(cell?.segments) && cell.segments.length > 0
    ? cell.segments.map(seg => ({
        start: (seg.start || '').slice(0, 5),
        end:   (seg.end   || '').slice(0, 5),
        classroom: seg.classroom || '',
      }))
    : [];
  const [mode, setMode] = useState(initialSegments.length > 0 ? 'segments' : 'single');
  const [segments, setSegments] = useState(initialSegments);
  const [start, setStart] = useState(cell?.start_time?.slice(0, 5) || '');
  const [end, setEnd] = useState(cell?.end_time?.slice(0, 5) || '');
  const [lunch, setLunch] = useState(cell?.lunch_break || '');
  // Default classroom: existing cell value → staff's primary classroom →
  // blank. Saves a dropdown click for every new shift.
  const [classroom, setClassroom] = useState(cell?.classroom || staff?.primary_classroom || '');
  const [status, setStatus] = useState(cell?.status || 'working');
  const [saving, setSaving] = useState(false);

  const sortedClassrooms = [...(classrooms || [])].sort((a, b) => a.display_order - b.display_order);
  const dayLabel = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const switchToSegments = () => {
    // Seed with the single shift's values so admin doesn't lose what they
    // already typed when flipping the toggle.
    if (segments.length === 0) {
      setSegments([{ start, end, classroom }]);
    }
    setMode('segments');
  };
  const switchToSingle = () => setMode('single');

  const addSegment = () => {
    if (segments.length >= 4) return;
    const last = segments[segments.length - 1];
    // New segment defaults to the staff's primary classroom — covers the
    // common case where they bounce back to their home room.
    setSegments([...segments, { start: last?.end || '', end: '', classroom: staff?.primary_classroom || last?.classroom || '' }]);
  };
  const updateSegment = (i, patch) => {
    setSegments(segments.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };
  const removeSegment = (i) => {
    setSegments(segments.filter((_, idx) => idx !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'working') {
      if (mode === 'segments') {
        if (segments.length === 0) return toast.error('Add at least one segment.');
        for (const s of segments) {
          if (!s.start || !s.end) return toast.error('Every segment needs a start and end time.');
        }
        // Overall shift envelope is the first segment's start → last
        // segment's end. Primary classroom is the first segment's room
        // (matches how getTeamWeek reports cell.classroom).
        const first = segments[0];
        const last  = segments[segments.length - 1];
        setSaving(true);
        try {
          await onSave({
            staff_id: staff.id, date,
            start_time: first.start, end_time: last.end,
            classroom: first.classroom || null,
            lunch_break: null, // segments encode the room transitions; no separate lunch
            status: 'working',
            segments,
          });
        } finally { setSaving(false); }
        return;
      }
      // single shift
      if (!start || !end) return toast.error('Start and end time are required for a working shift.');
    }
    setSaving(true);
    try {
      await onSave({
        staff_id: staff.id,
        date,
        start_time: status === 'working' ? start : null,
        end_time:   status === 'working' ? end   : null,
        classroom:  status === 'working' ? (classroom || null) : null,
        lunch_break: status === 'working' ? (lunch || null) : null,
        status,
        segments: null, // explicit null clears any prior segments
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <PencilSquareIcon className="w-5 h-5 text-orange-500" />
              {staff.first_name}'s shift
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{dayLabel}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {Array.isArray(cell?.partial_time_off) && cell.partial_time_off.length > 0 && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            {cell.partial_time_off.map((p, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-0.5">⚠</span>
                <div>
                  <div className="font-semibold">
                    Approved time off: {formatTime(p.start)}–{formatTime(p.end)}
                    {p.status === 'pending' && <span className="ml-1 font-normal">(pending)</span>}
                  </div>
                  {p.reason && <div className="text-amber-700 italic mt-0.5">"{p.reason}"</div>}
                  <div className="text-amber-700 mt-0.5">Schedule around this window.</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              <option value="working">Working</option>
              <option value="off">Off</option>
              <option value="time_off_approved">Off request (approved)</option>
              <option value="call_off">Call off (CO)</option>
              <option value="sick">Sick</option>
              <option value="holiday">Holiday</option>
            </select>
            {status !== 'working' && (
              <p className="text-[10px] text-gray-400 mt-1">
                Off request and Call off are usually set automatically when staff submit them through the portal. You can also set them here.
              </p>
            )}
          </div>

          {status === 'working' && (
            <>
              {/* Single ↔ Segments toggle */}
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={switchToSingle}
                  className={`px-3 py-1 rounded-lg font-medium ${mode === 'single' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  Single shift
                </button>
                <button type="button" onClick={switchToSegments}
                  className={`px-3 py-1 rounded-lg font-medium ${mode === 'segments' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  Split into segments
                </button>
              </div>

              {mode === 'single' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Start</label>
                      <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">End</label>
                      <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Lunch (optional)</label>
                    <input value={lunch} onChange={(e) => setLunch(e.target.value)}
                      placeholder="e.g. 12-1 or leave blank"
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                    <p className="text-[10px] text-gray-400 mt-1">
                      Short shifts often don't get a lunch — leave blank. Shifts 7+ hrs without a lunch flag for admin coverage.
                    </p>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Classroom</label>
                    <select value={classroom} onChange={(e) => setClassroom(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
                      <option value="">— Unassigned —</option>
                      {sortedClassrooms.map(c => (
                        <option key={c.id} value={c.name}>
                          {c.name}{c.max_kids_per_staff < 50 ? ` (1:${c.max_kids_per_staff})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {mode === 'segments' && (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-500">
                    Up to 4 segments. Each one is a continuous block in a single room (or admin/floats covering somewhere). Times should be back-to-back — segment 2 starts when segment 1 ends.
                  </p>
                  {segments.map((seg, i) => {
                    const theme = themeForRoom(seg.classroom, classrooms);
                    return (
                      <div key={i} className={`rounded-lg border ${theme.head} p-2`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-semibold uppercase text-gray-500">Segment {i + 1}</span>
                          {segments.length > 1 && (
                            <button type="button" onClick={() => removeSegment(i)}
                              className="text-[10px] text-red-600 hover:text-red-700 inline-flex items-center gap-0.5">
                              <TrashIcon className="w-3 h-3" /> remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-[1fr_1fr_1.5fr] gap-2">
                          <input type="time" value={seg.start}
                            onChange={(e) => updateSegment(i, { start: e.target.value })}
                            className="rounded border border-gray-200 px-2 py-1 text-xs" />
                          <input type="time" value={seg.end}
                            onChange={(e) => updateSegment(i, { end: e.target.value })}
                            className="rounded border border-gray-200 px-2 py-1 text-xs" />
                          <select value={seg.classroom}
                            onChange={(e) => updateSegment(i, { classroom: e.target.value })}
                            className="rounded border border-gray-200 px-2 py-1 text-xs bg-white">
                            <option value="">— Unassigned —</option>
                            {sortedClassrooms.map(c => (
                              <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  {segments.length < 4 && (
                    <button type="button" onClick={addSegment}
                      className="w-full text-xs text-orange-600 hover:text-orange-700 border border-dashed border-orange-300 rounded-lg py-1.5">
                      + Add segment ({segments.length}/4)
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-between pt-2">
            {cell?.override_id ? (
              <button type="button" onClick={() => onDelete(cell.override_id)}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg border border-red-200 hover:bg-red-50">
                <TrashIcon className="w-3.5 h-3.5" /> Remove shift
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button type="button" onClick={onCancel}
                className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// "Who's off this week" — approved (grey badge) + pending (amber badge)
// requests. Director sees this at the top BEFORE she starts scheduling
// so she doesn't accidentally put someone on who asked for the day off.
function TimeOffThisWeekBanner({ items }) {
  const parseDateOnly = (d) => {
    if (!d) return null;
    const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day);
  };
  const fmtDate = (d) => {
    const dt = parseDateOnly(d);
    return dt ? dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : d;
  };
  const fmtRange = (start, end) => start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`;
  const approved = items.filter(x => x.status === 'approved');
  const pending = items.filter(x => x.status === 'pending');
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-4">
      <div className="flex items-start gap-3">
        <CalendarDaysIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-yellow-900">
            Time off this week — don't schedule these staff
          </p>
          <p className="text-xs text-yellow-800 mt-0.5">
            {approved.length} approved
            {pending.length > 0 && ` · ${pending.length} pending (decide before publishing)`}
          </p>
          <ul className="mt-3 space-y-1 text-xs text-yellow-900">
            {items.map((r, i) => (
              <li key={`${r.request_id}-${i}`} className="flex items-start gap-2">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 uppercase ${
                  r.status === 'approved' ? 'bg-gray-100 text-gray-700 border border-gray-200' : 'bg-amber-100 text-amber-800 border border-amber-200'
                }`}>{r.status}</span>
                <span className="font-semibold">{r.staff_name}</span>
                <span>·</span>
                <span>{fmtRange(r.start_date, r.end_date)}</span>
                {!r.full_day && r.start_time && r.end_time && (
                  <span className="text-yellow-700">({formatTime(r.start_time)}–{formatTime(r.end_time)})</span>
                )}
                {r.reason && (
                  <span className="text-yellow-700 italic truncate">"{r.reason}"</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function LunchCoverageBanner({ alerts }) {
  const grouped = alerts.reduce((acc, a) => {
    if (!acc[a.date]) acc[a.date] = [];
    acc[a.date].push(a);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {alerts.length} shift{alerts.length === 1 ? '' : 's'} need{alerts.length === 1 ? 's' : ''} admin lunch coverage this week
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            Shifts of 7+ hours without a scheduled lunch — you'll need to arrange a break.
          </p>
          <div className="mt-3 space-y-1.5">
            {dates.map(d => (
              <div key={d} className="text-xs text-amber-900">
                <span className="font-semibold">
                  {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}:
                </span>{' '}
                {grouped[d].map((a, i) => (
                  <span key={a.staff_id}>
                    <StaffLink id={a.staff_id} name={a.staff_name} className="text-amber-900 underline decoration-amber-400 hover:text-amber-700" />
                    <span className="text-amber-700"> ({formatTime(a.start_time)}–{formatTime(a.end_time)}, {a.shift_hours} hr)</span>
                    {i < grouped[d].length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Import flow ───────────────────────────────────────────

function ImportFlow({ onSaved }) {
  const [mode, setMode] = useState('file'); // 'file' | 'paste' | 'ai'
  const [pasted, setPasted] = useState('');
  const [file, setFile] = useState(null);
  const [weekStart, setWeekStart] = useState(formatISO(startOfWeek(new Date())));
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [sendEmails, setSendEmails] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiHistoryWeeks, setAiHistoryWeeks] = useState(2);
  const [aiNotes, setAiNotes] = useState(null);

  useEffect(() => {
    api.get('/staff').then(({ data }) => setStaffList(data)).catch(() => {});
    // Live classrooms drive the room dropdowns + colors in the review grid,
    // so the view always matches what's configured under Classrooms & Ratios.
    api.get('/classrooms').then(({ data }) => setClassrooms(data)).catch(() => {});
  }, []);

  const parse = async () => {
    setParsing(true);
    setAiNotes(null);
    try {
      let data;
      if (mode === 'file') {
        if (!file) { toast.error('Choose an Excel file first'); setParsing(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('week_start', weekStart);
        ({ data } = await api.post('/team-schedule/parse-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }));
      } else if (mode === 'paste') {
        if (!pasted.trim()) { toast.error('Paste your schedule first'); setParsing(false); return; }
        ({ data } = await api.post('/team-schedule/parse', { pasted_text: pasted, week_start: weekStart }));
      } else {
        // mode === 'ai'
        ({ data } = await api.post('/team-schedule/generate', {
          week_start: weekStart,
          history_weeks: aiHistoryWeeks,
        }));
        if (data.ai_notes) setAiNotes(data.ai_notes);
      }
      setParsed(data);
      const verb = mode === 'ai' ? 'Drafted' : 'Parsed';
      toast.success(`${verb} ${data.shifts.length} shifts — review below before saving`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const updateShift = (idx, patch) => {
    setParsed(p => ({
      ...p,
      shifts: p.shifts.map((s, i) => i === idx ? recomputeCoverage({ ...s, ...patch }) : s),
    }));
  };

  const removeShift = (idx) => {
    setParsed(p => ({ ...p, shifts: p.shifts.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!parsed) return;
    const valid = parsed.shifts.filter(s => s.staff_id && s.date && s.start_time && s.end_time);
    if (valid.length === 0) return toast.error('No valid shifts to save (every shift needs a staff match + date + start/end time)');
    setSaving(true);
    try {
      const { data } = await api.post('/team-schedule/import', {
        shifts: valid,
        send_emails: sendEmails,
      });
      toast.success(`${data.shifts_saved} shifts saved${data.emails_sent > 0 ? ` · ${data.emails_sent} emails sent` : ''}`);
      setPasted(''); setParsed(null);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Step 1 — Bring in your schedule</h3>
        <p className="text-xs text-gray-500 mb-3">
          Upload your spreadsheet (best — preserves the grid structure) or paste cells. AI will figure
          out who works when. You'll review the result before anything saves.
        </p>

        <div className="flex items-center gap-3 mb-3">
          <label className="text-sm text-gray-700">Week starting (Monday):</label>
          <input type="date" min={DATE_FLOOR} value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setMode('file')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${mode === 'file' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Upload .xlsx
          </button>
          <button onClick={() => setMode('paste')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${mode === 'paste' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Paste cells
          </button>
          <button onClick={() => setMode('ai')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1 ${mode === 'ai' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <SparklesIcon className="w-4 h-4" /> AI generate from history
          </button>
        </div>

        {mode === 'file' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">
              In Google Sheets: <strong>File → Download → Microsoft Excel (.xlsx)</strong>, then upload below.
              You can upload the entire workbook (with all weekly tabs) — we'll auto-pick the right
              sheet based on the week you selected.
            </p>
            <label className="block">
              <input type="file" accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-50 file:text-orange-700 file:font-medium hover:file:bg-orange-100" />
            </label>
            {file && <p className="text-xs text-gray-500 mt-2">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
          </div>
        )}

        {mode === 'paste' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">
              Select all your schedule cells (names + each day's 3 rows), copy, paste below.
              <strong className="text-amber-600"> Heads up:</strong> pasting from Sheets sometimes loses cell structure;
              if the AI gets confused, switch to "Upload .xlsx".
            </p>
            <textarea value={pasted} onChange={(e) => setPasted(e.target.value)}
              placeholder="Paste your weekly schedule here…"
              rows={10}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-mono focus:border-orange-500 focus:outline-none resize-y" />
          </div>
        )}

        {mode === 'ai' && (
          <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-4">
            <p className="text-xs text-gray-700 mb-3">
              <strong>Draft this week's schedule from the last few weeks of imported shifts.</strong>{' '}
              The AI looks at each staff member's typical pattern, honors approved time-off, and
              keeps shifts within target weekly hours. You'll review every shift before saving —
              edit anything that's off.
            </p>
            <div className="flex items-center gap-3 mb-2">
              <label className="text-xs text-gray-700">History to learn from:</label>
              <select value={aiHistoryWeeks} onChange={(e) => setAiHistoryWeeks(Number(e.target.value))}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white">
                <option value={1}>Last 1 week</option>
                <option value={2}>Last 2 weeks</option>
                <option value={3}>Last 3 weeks</option>
                <option value={4}>Last 4 weeks</option>
              </select>
            </div>
            <p className="text-[11px] text-gray-500">
              Needs at least one previously imported week. Approved time-off requests are honored
              automatically. The AI's "rationale" for each shift appears in the review table.
            </p>
          </div>
        )}

        <div className="flex justify-end mt-3">
          <button onClick={parse}
            disabled={parsing || (mode === 'file' ? !file : mode === 'paste' ? !pasted.trim() : false)}
            className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
            <SparklesIcon className="w-4 h-4" />
            {parsing
              ? (mode === 'ai' ? 'Drafting…' : 'Parsing…')
              : (mode === 'ai' ? 'Generate draft' : 'Parse with AI')}
          </button>
        </div>
      </div>

      {aiNotes && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-sm text-orange-900">
          <p className="font-semibold mb-1 inline-flex items-center gap-2">
            <SparklesIcon className="w-4 h-4" /> AI scheduling notes
          </p>
          <p className="text-xs leading-relaxed">{aiNotes}</p>
        </div>
      )}

      {/* Phase 2: enrollment usage indicator + coverage gap warnings */}
      {parsed?.enrollment_summary && parsed.enrollment_summary.cells_filled > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 text-xs text-blue-900">
          <p>
            <span className="font-semibold">Used enrollment data:</span>{' '}
            {parsed.enrollment_summary.cells_filled} of {parsed.enrollment_summary.total_cells_possible}{' '}
            day-room cells filled ({parsed.enrollment_summary.kid_rooms.length} kid-rooms).{' '}
            <a href="/enrollment-forecast" className="underline hover:text-blue-700">
              Edit forecast →
            </a>
          </p>
        </div>
      )}
      {parsed?.enrollment_summary && parsed.enrollment_summary.cells_filled === 0 && parsed?.source === 'ai_generated' && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 text-xs text-gray-600">
          <p>
            <span className="font-semibold">No enrollment forecast for this week.</span>{' '}
            The AI scheduled by past pattern only — it didn't apply licensing-ratio coverage rules.{' '}
            <a href="/enrollment-forecast" className="text-orange-600 underline hover:text-orange-700">
              Add a forecast →
            </a>
          </p>
        </div>
      )}
      {parsed?.coverage_gaps?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
          <p className="text-sm font-semibold text-red-900 mb-2 inline-flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
            {parsed.coverage_gaps.length} room{parsed.coverage_gaps.length === 1 ? '' : 's'} short of licensing ratio
          </p>
          <ul className="space-y-0.5 text-xs text-red-900">
            {parsed.coverage_gaps.map((g, i) => (
              <li key={i}>
                <span className="font-medium">{new Date(g.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}:</span>{' '}
                {g.classroom} — assigned {g.assigned}, need {g.required}
                {g.expected_kids != null && <span className="text-red-700"> ({g.expected_kids} kids)</span>}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-red-700 mt-1.5">
            Cover with PRN staff, swap classroom assignments below, or push back enrollment.
          </p>
        </div>
      )}

      {parsed && (
        <ReviewByDayClassroom
          parsed={parsed}
          staffList={staffList}
          classrooms={classrooms}
          updateShift={updateShift}
          removeShift={removeShift}
          sendEmails={sendEmails}
          setSendEmails={setSendEmails}
          save={save}
          saving={saving}
        />
      )}
    </div>
  );
}

// ─── Review parsed shifts: by day → by classroom (color-coded) ────
//
// Each day is its own card, shifts inside grouped by classroom. Names,
// colors, and ratios all come live from /api/classrooms — whatever's
// configured under Classrooms & Ratios drives this view.
// "Unassigned" bubbles to the top so admin can pick a room before saving.

// Classroom color helpers (COLOR_TO_TW, themeForRoom, sortRooms, …) now live
// in ../../lib/classroomTheme so the staff schedule shares the exact palette.
// Imported at the top of this file.

function ReviewByDayClassroom({ parsed, staffList, classrooms, updateShift, removeShift, sendEmails, setSendEmails, save, saving }) {
  const matched = parsed.shifts.filter(s => s.staff_id).length;
  const needCount = parsed.shifts.filter(s => s.needs_lunch_coverage).length;

  // Group by date → then by classroom (Unassigned bucket for missing).
  const byDate = parsed.shifts.reduce((acc, s, idx) => {
    const d = s.date || 'undated';
    if (!acc[d]) acc[d] = {};
    const room = s.classroom || 'Unassigned';
    if (!acc[d][room]) acc[d][room] = [];
    acc[d][room].push({ ...s, _idx: idx });
    return acc;
  }, {});
  const sortedDates = Object.keys(byDate).sort();

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Step 2 — Review parsed shifts</h3>
        <p className="text-xs text-gray-500">
          Grouped by day and classroom. Colors match the Excel legend.{' '}
          <strong className="text-gray-700">{matched} of {parsed.shifts.length}</strong> rows matched a staff member —
          fix any unmatched (amber) before saving. Click ✕ to drop a shift you don't want to import.
        </p>

        {staffList.length === 0 && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-900">
            No active staff in the portal yet — that's why every name shows "Unmatched". Add staff under{' '}
            <strong>Staff Directory</strong> first; the parser will auto-match them by first name.
          </div>
        )}
        {needCount > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span><strong>{needCount}</strong> shift{needCount === 1 ? '' : 's'} of 7+ hours have no lunch scheduled — admin will need to arrange coverage.</span>
          </div>
        )}
      </div>

      {/* One card per day */}
      {sortedDates.map(dateISO => {
        const rooms = byDate[dateISO];
        const allShiftsForDay = Object.values(rooms).flat();
        const dayDate = dateISO !== 'undated'
          ? new Date(dateISO + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
          : 'No date';
        const dayCoverageGaps = allShiftsForDay.filter(s => s.needs_lunch_coverage).length;
        const sortedRooms = sortRooms(Object.keys(rooms), classrooms);

        return (
          <div key={dateISO} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{dayDate}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {allShiftsForDay.length} shift{allShiftsForDay.length === 1 ? '' : 's'}
                  {dayCoverageGaps > 0 && (
                    <span className="text-amber-700"> · {dayCoverageGaps} need lunch coverage</span>
                  )}
                </p>
              </div>
            </div>

            <div className="p-3 space-y-2">
              {sortedRooms.map(room => {
                const theme = themeForRoom(room, classrooms);
                const shifts = rooms[room];
                const roomCfg = classrooms.find(c => c.name === room);
                const ratio = roomCfg && roomCfg.max_kids_per_staff < 50
                  ? `1:${roomCfg.max_kids_per_staff}` : null;
                return (
                  <div key={room} className={`rounded-xl border ${theme.head} overflow-hidden`}>
                    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-current/10">
                      <span className={`w-2 h-6 rounded-sm ${theme.bar}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${theme.pill}`}>
                        {room}
                      </span>
                      <span className="text-[11px] text-gray-500">{shifts.length} shift{shifts.length === 1 ? '' : 's'}</span>
                      {ratio && <span className="text-[11px] text-gray-400">· ratio {ratio}</span>}
                    </div>

                    <div className="divide-y divide-white/60">
                      {shifts.map(s => (
                        <ShiftRow key={s._idx} s={s} idx={s._idx}
                          staffList={staffList}
                          classrooms={classrooms}
                          updateShift={updateShift}
                          removeShift={removeShift} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Save bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={sendEmails} onChange={(e) => setSendEmails(e.target.checked)} className="accent-orange-500" />
          <EnvelopeIcon className="w-4 h-4" /> Email each staff their week after saving
        </label>
        <button onClick={save} disabled={saving}
          className="bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
          <ArrowUpTrayIcon className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
}

function ShiftRow({ s, idx, staffList, classrooms, updateShift, removeShift }) {
  const unmatched = !s.staff_id;
  const rowClass = unmatched ? 'bg-amber-50/50 hover:bg-amber-50' : 'bg-white hover:bg-gray-50';
  // Sort classrooms by display_order (live data) for the dropdown
  const sortedClassrooms = [...(classrooms || [])].sort((a, b) => a.display_order - b.display_order);
  return (
    <div className={`px-3 py-2 grid grid-cols-[1.5fr_auto_auto_1fr_auto] gap-2 items-center ${rowClass}`}>
      {/* Staff picker */}
      <div>
        <select value={s.staff_id || ''}
          onChange={(e) => updateShift(idx, { staff_id: e.target.value ? Number(e.target.value) : null })}
          className={`w-full rounded-lg border px-2 py-1 text-xs bg-white ${unmatched ? 'border-amber-400 text-amber-800' : 'border-gray-200'}`}>
          <option value="">{unmatched ? `— ${s.staff_name || '?'} (no match)` : '— pick a staff member —'}</option>
          {staffList.map(st => <option key={st.id} value={st.id}>{st.first_name} {st.last_name}</option>)}
        </select>
      </div>

      {/* Times */}
      <div className="flex items-center gap-1">
        <input type="time" value={s.start_time || ''}
          onChange={(e) => updateShift(idx, { start_time: e.target.value })}
          className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-xs" />
        <span className="text-gray-400 text-xs">–</span>
        <input type="time" value={s.end_time || ''}
          onChange={(e) => updateShift(idx, { end_time: e.target.value })}
          className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-xs" />
      </div>

      {/* Lunch */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-gray-400">lunch</span>
        <input value={s.lunch_break || ''}
          onChange={(e) => updateShift(idx, { lunch_break: e.target.value })}
          placeholder="—"
          className={`w-16 rounded-lg border px-2 py-1 text-xs ${s.needs_lunch_coverage ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`} />
        {s.needs_lunch_coverage && (
          <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
            title={`${s.shift_hours || ''} hr shift — no lunch scheduled. Admin will need to arrange coverage.`} />
        )}
      </div>

      {/* Classroom — dropdown sourced from /api/classrooms */}
      <div>
        <select value={s.classroom || ''}
          onChange={(e) => updateShift(idx, { classroom: e.target.value || null })}
          className={`w-full rounded-lg border px-2 py-1 text-xs bg-white ${!s.classroom ? 'border-amber-300 text-amber-700' : 'border-gray-200'}`}
          title="Classroom assignment">
          <option value="">— Unassigned —</option>
          {sortedClassrooms.map(c => (
            <option key={c.id} value={c.name}>
              {c.name}{c.max_kids_per_staff < 50 ? ` (1:${c.max_kids_per_staff})` : ''}
            </option>
          ))}
        </select>
      </div>

      <button onClick={() => removeShift(idx)} className="text-gray-300 hover:text-red-500" title="Drop this shift">
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
